/**
 * CreatureAbilityCard
 * L4-TAP-002: Individual creature card with ability cooldown display
 *
 * Shows creature portrait, name, ability icons with cooldown sweep overlays,
 * and a mini HP bar. Dead creatures are grayed out. Clicking pans camera.
 *
 * All dimensions driven by `sizing` prop from parent TeamAbilityPanel,
 * which computes sizes based on creature count (fewer = larger cards).
 */

import React from 'react';
import styles from './CreatureAbilityCard.module.css';

/** Element color map (CSS hex) matching cooldown-indicator-renderer.ts */
const ELEMENT_COLORS: Record<string, string> = {
  physical: '#888888',
  fire: '#FF4400',
  ice: '#00CCFF',
  lightning: '#FFFF00',
  poison: '#00FF00',
  dark: '#8800AA',
  holy: '#FFD700',
  arcane: '#AA44FF',
  nature: '#228B22',
};

export interface AbilityDisplayData {
  id: string;
  name: string;
  element: string;
  delivery?: string;
  cooldownRemaining: number;
  cooldownTicks: number;
}

/** Sizing config computed by TeamAbilityPanel based on creature count */
export interface CardSizing {
  portraitSize: number;
  iconSize: number;
  fontSize: number;
  hpBarHeight: number;
  cardPadding: number;
  showName: boolean;
}

export interface CreatureAbilityCardProps {
  unitId: string;
  name: string;
  portraitUrl?: string;
  health: number;
  maxHealth: number;
  status: 'alive' | 'dead';
  abilities: AbilityDisplayData[];
  isCompact: boolean;
  sizing: CardSizing;
  onClick: () => void;
}

/** Calculate HP bar color based on health percentage */
function getHpColor(ratio: number): string {
  if (ratio > 0.66) return '#4CAF50';   // green
  if (ratio > 0.33) return '#FFC107';   // yellow
  return '#F44336';                       // red
}

/**
 * Calculate cooldown sweep degrees for conic-gradient.
 * Returns 0-360 where 360 = full sweep (fully on cooldown), 0 = ready.
 */
function getSweepDeg(remaining: number, total: number): number {
  if (total <= 0 || remaining <= 0) return 0;
  return Math.min(360, (remaining / total) * 360);
}

function CreatureAbilityCardInner({
  name,
  portraitUrl,
  health,
  maxHealth,
  status,
  abilities,
  isCompact,
  sizing,
  onClick,
}: CreatureAbilityCardProps): JSX.Element {
  const isDead = status === 'dead';
  const initial = name.charAt(0).toUpperCase();
  const hpRatio = maxHealth > 0 ? Math.max(0, health / maxHealth) : 0;
  const hpPercent = Math.round(hpRatio * 100);

  const { portraitSize, iconSize, fontSize, hpBarHeight, cardPadding, showName } = sizing;

  return (
    <button
      className={`${styles.card} ${isDead ? styles.dead : ''} ${isCompact ? styles.compact : ''}`}
      style={{ padding: `${cardPadding}px ${Math.max(3, cardPadding - 2)}px` }}
      onClick={onClick}
      title={name}
      aria-label={`${name}, ${isDead ? 'defeated' : `${hpPercent}% HP`}`}
    >
      {/* Portrait */}
      <div className={styles.portrait} style={{ width: portraitSize, height: portraitSize }}>
        {portraitUrl ? (
          <img
            src={portraitUrl}
            alt={name}
            className={styles.portraitImg}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const fallback = e.currentTarget.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
        ) : null}
        <span
          className={styles.initial}
          style={{
            display: portraitUrl ? 'none' : 'flex',
            fontSize: Math.max(10, Math.round(portraitSize * 0.45)),
          }}
        >
          {initial}
        </span>
      </div>

      {/* Name */}
      {showName && (
        <div
          className={styles.name}
          style={{ fontSize, maxWidth: isCompact ? 60 : undefined }}
          title={name}
        >
          {name}
        </div>
      )}

      {/* Ability icons grid */}
      <div className={styles.abilities} style={{ gap: Math.max(2, Math.round(iconSize * 0.12)) }}>
        {abilities.map((ability) => {
          const sweepDeg = getSweepDeg(ability.cooldownRemaining, ability.cooldownTicks);
          const isOnCooldown = sweepDeg > 0;
          const borderColor = ELEMENT_COLORS[ability.element] || ELEMENT_COLORS.physical;

          return (
            <div
              key={ability.id}
              className={styles.abilityIcon}
              style={{ width: iconSize, height: iconSize }}
              title={`${ability.name}${isOnCooldown ? ` (${Math.ceil(ability.cooldownRemaining / 60)}s)` : ' (Ready)'}`}
            >
              <img
                src={`/assets/ability-icons/${ability.delivery === 'buff' ? 'buff' : ability.element}.png`}
                alt={ability.name}
                className={styles.iconImg}
                style={{ borderColor }}
              />
              {isOnCooldown && (
                <div
                  className={styles.cooldownSweep}
                  style={{
                    background: `conic-gradient(from 0deg, rgba(0,0,0,0.7) 0deg, rgba(0,0,0,0.7) ${sweepDeg}deg, transparent ${sweepDeg}deg)`,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Mini HP bar */}
      <div className={styles.hpBar} style={{ height: hpBarHeight }}>
        <div
          className={styles.hpFill}
          style={{
            width: `${hpPercent}%`,
            backgroundColor: getHpColor(hpRatio),
          }}
        />
      </div>
    </button>
  );
}

/** Custom comparator: only re-render on meaningful combat state changes */
function arePropsEqual(prev: CreatureAbilityCardProps, next: CreatureAbilityCardProps): boolean {
  if (prev.health !== next.health) return false;
  if (prev.status !== next.status) return false;
  if (prev.isCompact !== next.isCompact) return false;
  if (prev.sizing !== next.sizing) return false;
  if (prev.abilities.length !== next.abilities.length) return false;
  for (let i = 0; i < prev.abilities.length; i++) {
    if (prev.abilities[i].cooldownRemaining !== next.abilities[i].cooldownRemaining) return false;
  }
  return true;
}

export const CreatureAbilityCard = React.memo(CreatureAbilityCardInner, arePropsEqual);
